import { ArrowForwardIosOutlined } from "@mui/icons-material";
import { Card, CardActionArea, IconButton } from "@mui/material";
import ConfirmationDialog from "components/confirmationdialog";
import {
  MobileItemDescription,
  MobileItemValue,
} from "components/table/styled.components";
import keys from "locale/keys";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { SURVEY_POPUP } from "utils/constants";
import {
  appendPoints,
  formatConversion,
  formatScore,
  setPopupShown,
} from "utils/Utils";
import { isPopupShown } from "utils/Utils";

import calendar from "../../../assets/icons/calendar.svg";
import { SurveyItemProps } from "./interface";
import {
  ItemText,
  PointsItem,
  PointsItemWrapper,
  StackItem,
  StartButton,
  StartWrapper,
  SurveyItemWrapper,
} from "./styled.components";

const SurveyItem = (props: SurveyItemProps) => {
  const {
    surveyNumber,
    id,
    timeLeft = "",
    points,
    conversion,
    score,
    perClick,
    totalQuotas,
    totalRemaining,
    isCompletedSurveys,
    completedDate,
    url,
    providerKey,
    onStartSurveyClicked,
    isMobile,
    timeToComplete,
    status,
  } = props;
  const { t } = useTranslation();
  const [showSurveyPopup, setShowSurveyPopup] = useState(false);
  const isProd = process.env.REACT_APP_ENV === "prod";

  const onStartClicked = () => {
    if (!isPopupShown()) {
      setShowSurveyPopup(true);
      setPopupShown();
    } else {
      // If popup has been shown before, directly start the survey
      onStartSurveyClicked?.(url, providerKey, id);
    }
  };

  const handleSurveyPopupDismiss = () => {
    setShowSurveyPopup(false);
  };

  const handleContinueToSurvey = () => {
    setShowSurveyPopup(false);
    onStartSurveyClicked?.(url, providerKey, id);
  };

  let content;

  if (isMobile) {
    content = (
      <Card>
        <CardActionArea onClick={() => onStartClicked()}>
          <SurveyItemWrapper>
            <StackItem direction="row" alignItems="center" gap={1} width="100%">
              <ItemText issurveyname={true}>{surveyNumber}</ItemText>
              <IconButton>
                <ArrowForwardIosOutlined fontSize="small"></ArrowForwardIosOutlined>
              </IconButton>
            </StackItem>
            <table>
              <tr>
                <MobileItemDescription>
                  {isCompletedSurveys
                    ? `${t(keys.completed_date)} : `
                    : `${t(keys.end_date)} : `}
                </MobileItemDescription>
                <MobileItemValue>
                  <StackItem direction="row" alignItems="center" gap={0}>
                    <img src={calendar} alt="calendar" />
                    <MobileItemValue>
                      {isCompletedSurveys ? completedDate : timeLeft}
                    </MobileItemValue>
                  </StackItem>
                </MobileItemValue>
              </tr>
              <tr>
                <MobileItemDescription>
                  {t(keys.points)} :
                </MobileItemDescription>
                <MobileItemValue>
                  <PointsItem>{appendPoints(points)}</PointsItem>
                </MobileItemValue>
              </tr>
              {!isCompletedSurveys && (
                <>
                  {!isProd && (
                    <tr>
                      <MobileItemDescription>
                        {t(keys.conversion)} :
                      </MobileItemDescription>
                      <MobileItemValue>
                        {formatConversion(conversion)}
                      </MobileItemValue>
                    </tr>
                  )}
                  {!isProd && (
                    <tr>
                      <MobileItemDescription>
                        {t(keys.score)} :
                      </MobileItemDescription>
                      <MobileItemValue>{formatScore(score)}</MobileItemValue>
                    </tr>
                  )}
                  <tr>
                    <MobileItemDescription>
                      {t(keys.survey_length)} :
                    </MobileItemDescription>
                    <MobileItemValue>{timeToComplete}</MobileItemValue>
                  </tr>
                  {!isProd && (
                    <>
                      <tr>
                        <MobileItemDescription>
                          {t(keys.per_click)} :
                        </MobileItemDescription>
                        <MobileItemValue>{perClick}</MobileItemValue>
                      </tr>
                      <tr>
                        <MobileItemDescription>
                          {t(keys.total_remaining)} :
                        </MobileItemDescription>
                        <MobileItemValue>{totalRemaining}</MobileItemValue>
                      </tr>
                      <tr>
                        <MobileItemDescription>
                          {t(keys.quotas)} :
                        </MobileItemDescription>
                        <MobileItemValue>{totalQuotas}</MobileItemValue>
                      </tr>
                    </>
                  )}
                </>
              )}
              {isCompletedSurveys && (
                <tr>
                  <MobileItemDescription>
                    {t(keys.status)} :
                  </MobileItemDescription>
                  <MobileItemValue>{status}</MobileItemValue>
                </tr>
              )}
            </table>
          </SurveyItemWrapper>
        </CardActionArea>
      </Card>
    );
  } else if (isCompletedSurveys) {
    content = (
      <SurveyItemWrapper>
        <ItemText>{surveyNumber}</ItemText>
        <PointsItemWrapper>
          <PointsItem>{appendPoints(points)}</PointsItem>
        </PointsItemWrapper>
        <StackItem direction="row" alignItems="center" gap={1}>
          <img src={calendar} alt="calendar" />
          <ItemText>{completedDate}</ItemText>
        </StackItem>
        <ItemText>{status}</ItemText>
      </SurveyItemWrapper>
    );
  } else {
    content = (
      <SurveyItemWrapper>
        <ItemText>{timeToComplete}</ItemText>
        {!isProd && <ItemText>{formatConversion(conversion)}</ItemText>}
        <ItemText>
          <PointsItem>{appendPoints(points)}</PointsItem>
        </ItemText>
        {!isProd && <ItemText>{formatScore(score)}</ItemText>}
        {!isProd && (
          <>
            <ItemText>{perClick}</ItemText>
            <ItemText>{totalRemaining}</ItemText>
            <ItemText>{totalQuotas}</ItemText>
          </>
        )}

        <StackItem direction="row" alignItems="center" gap={1}>
          <img src={calendar} alt="calendar" />
          <ItemText>{timeLeft}</ItemText>
        </StackItem>
        <ItemText>{surveyNumber}</ItemText>
        <StartWrapper>
          <StartButton variant="outlined" onClick={() => onStartClicked()}>
            {t(keys.start_survey)}
          </StartButton>
        </StartWrapper>
      </SurveyItemWrapper>
    );
  }

  return (
    <>
      {content}
      <ConfirmationDialog
        show={showSurveyPopup}
        dismiss={handleSurveyPopupDismiss}
        type={0}
        heading={SURVEY_POPUP.TITLE}
        msg={SURVEY_POPUP.DESCRIPTION}
        buttonText={SURVEY_POPUP.BUTTON_TEXT}
        onButtonClickHandler={handleContinueToSurvey}
        hideIcon={true}
        headingAlign="left"
        messageAlign="left"
      />
    </>
  );
};

export default SurveyItem;
